import { memo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Typography, IconButton } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { ProductsTableItem } from "../../../../../../modules/merchant/admin/products/productsTypes"
import { SearchableText } from "../../../../search/Searchable"
import { ProductDetailsDialog } from "./ProductDetailsDialog"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { Tag } from "./Tag"

const MAX_CHARS = 42

const useStyles = makeStyles()((theme) => ({
    productTitle: {
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,
        overflow: "hidden",
        minHeight: "3rem",
        lineHeight: "1.5rem",
        overflowWrap: "anywhere",
        wordBreak: "normal",
    },
    tagLine: {
        display: "flex",
        alignItems: "center",
        gap: "0.375rem",
        flexWrap: "wrap",
    },
    onlyLeftTag: {
        display: "inline-flex",
        alignItems: "center",
        padding: `0 ${theme.spacing(1)}`,
        color: theme.palette.common.white,
        backgroundColor: theme.palette.common.black,
        borderRadius: "0.25rem",
    },
    iconWrap: {
        display: "inline-flex",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
    },
    iconButton: {
        padding: "0",
        color: theme.palette.tailwind.slate[400],
        transition: "all 0.3s ease-out",
        marginLeft: ".25rem",
        fontSize: "1rem",
        lineHeight: "1.5rem",
        "&:hover": {
            color: theme.palette.tailwind.slate[500],
        },
    },
}))

type ProductDetailsContentProps = {
    product: ProductsTableItem
    searchRegex: RegExp | undefined
    marketingTag?: string
    productsOnSale?: number
    userId: number
}

export const ProductDetailsContent = memo(
    ({ product, searchRegex, marketingTag, productsOnSale, userId }: ProductDetailsContentProps) => {
        const { classes } = useStyles()
        const [productId, setProductId] = useState<number | null>(null)

        const handleDetailsClick = () => {
            setProductId(product.id)
        }

        const productTitleLength = product.name.length
        const isOverLimit = productTitleLength > MAX_CHARS

        const title = isOverLimit ? product.name.slice(0, MAX_CHARS) : product.name
        const showTagLine = Boolean(marketingTag || productsOnSale)

        return (
            <>
                {showTagLine && (
                    <Box className={classes.tagLine}>
                        {marketingTag && <Tag>{marketingTag}</Tag>}
                        {!!productsOnSale && (
                            <Box className={classes.onlyLeftTag}>
                                <LocalizedStrict id="offer-tag-limited-on-sale" vars={{ amount: productsOnSale }}>
                                    <Typography variant="caption">{productsOnSale} left</Typography>
                                </LocalizedStrict>
                            </Box>
                        )}
                    </Box>
                )}
                <SearchableText search={searchRegex}>
                    <Typography variant="subtitle1" className={classes.productTitle}>
                        {title}
                        {isOverLimit && "..."}
                        {"\u00A0"}
                        <Typography className={classes.iconWrap}>
                            <IconButton onClick={handleDetailsClick} className={classes.iconButton} size="small">
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </IconButton>
                        </Typography>
                    </Typography>
                </SearchableText>

                {productId && (
                    <ProductDetailsDialog
                        userId={userId}
                        productId={productId}
                        handleClose={() => setProductId(null)}
                    />
                )}
            </>
        )
    },
)
